import impulse from "./modules/impulse";
import period from "./modules/period";
import monthly from "./modules/monthly";
import filter from "./modules/filters";
import coinsList from "./modules/coinsList";
import widget from "./modules/widget";
import dynamics from "./modules/dynamics";

const PAGE_SIZE = 100;

const Scanner = {
  namespaced: true,

  state: () => ({
    coins: [],
    compareCoin: null,
    isLoadingCoinsList: false,
    isLoadingNewCoinsList: false,
    isReadyCoinsList: false,
    isNextCoinsList: true,
    nextPageCoinsListNumber: 1,
    contextCoinsList: 'dynamics',
    isOpenInnerContent: null,
    preselectedImpulseId: null,
  }),

  actions: {
    async initScanner({ dispatch, state, getters }){
      await dispatch('initDynamics');
      await dispatch('initImpulse');
      await dispatch('initPeriod');
      await dispatch('loadCoinsList', {});
      await dispatch('filter/initCategories');
      await dispatch('filter/initCustomTags');
    },

    async initDynamics({ dispatch, state, getters }){
      let params = {};
      const base_coin_id = getters['coinsList/getActiveCoinID'];
      const isUsd = getters['coinsList/getIsUSD'];
      if(isUsd) params['usd'] = true;
      if(base_coin_id) params['base_coin_id'] = base_coin_id;
      await dispatch('dynamics/updateDynamicsList', params);
      if(!state.compareCoin?.id) return;
      await dispatch('dynamics/loadCompare', { base_coin_id: base_coin_id, compare_id: state.compareCoin.id });
    },

    async initImpulse({ dispatch, state, getters, commit }) {
      const base_coin_id = getters['coinsList/getActiveCoinID'];
      const isUsd = getters['coinsList/getIsUSD'];
      let params = {};
      if (base_coin_id) params.base_coin_id = base_coin_id;
      if (isUsd) params.usd = true;
      if (state.preselectedImpulseId) {
        commit('SET_COINS_LIST_CONTEXT', 'impulse');
      }
      const impulses = await dispatch('impulse/updateImpulseList', params);
      const activeId = state.preselectedImpulseId || getters['impulse/activeImpulseId'] || impulses?.[0]?.impulse_id;
      if (activeId) {
        await dispatch('impulse/setActiveImpulseId', activeId);
      }
      if (state.compareCoin?.id) {
        await dispatch('impulse/loadCompare', { base_coin_id, compare_id: state.compareCoin.id });
      }
      commit('SET_PRESELECTED_IMPULSE_ID', null);
    },

    async initPeriod({ dispatch, state, getters }){
      let params = {};
      const base_coin_id = getters['coinsList/getActiveCoinID'];
      if(base_coin_id) params['base_coin_id'] = base_coin_id;
      const isUsd = getters['coinsList/getIsUSD'];
      if(isUsd) params['usd'] = true;
      await dispatch('period/updatePeriodList', params);
      if(!state.compareCoin?.id) return;
      await dispatch('period/loadCompare', { base_coin_id: base_coin_id, compare_id: state.compareCoin.id});
    },

    async initMonthly({ dispatch, state, getters }){
      let params = {};
      const base_coin_id = getters['coinsList/getActiveCoinID'];
      const isUsd = getters['coinsList/getIsUSD'];
      if(base_coin_id) params['base_coin_id'] = base_coin_id;
      if(isUsd) params['usd'] = true;
      await dispatch('monthly/updateMonthlyList', params);
      if(!state.compareCoin?.id) return;
      await dispatch('monthly/loadCompare', { base_coin_id: base_coin_id, compare_id: state.compareCoin.id, page: 1 });
    },

    async activeGeneralCoinFilter({ dispatch, getters, state }, id){
      await dispatch('coinsList/activeGeneralId', id);
      const coinId = getters['coinsList/getActiveCoinID'];
      const isUsd = getters['coinsList/getIsUSD'];
      await dispatch('loadCoinsList', false);
      if(getters['dynamics/isOpenedList']){
        await dispatch('dynamics/updateDynamicsList', { base_coin_id: coinId, usd: isUsd });
        if(state.compareCoin?.id){
          await dispatch('dynamics/loadCompare', { base_coin_id: coinId, compare_id: state.compareCoin.id, usd: isUsd });
        }
      }
      if(getters['impulse/isOpenedList']){
        await dispatch('impulse/updateImpulseList', { base_coin_id: coinId, usd: isUsd });
        if(state.compareCoin?.id){
          await dispatch('impulse/loadCompare', { base_coin_id: coinId, compare_id: state.compareCoin.id, usd: isUsd });
        }
      }
      if(getters['period/isOpenedList']){
        await dispatch('period/updatePeriodList', { base_coin_id: coinId, usd: isUsd });
        if(state.compareCoin?.id){
          await dispatch('period/loadCompare', { base_coin_id: coinId, compare_id: state.compareCoin.id, usd: isUsd });
        }
      }
      if(getters['monthly/isOpenedList']){
        await dispatch('initMonthly');
      }
    },

    async activeTopFilter({ dispatch }, { top }){
      await dispatch('resetCompareData');
      dispatch('filter/activeTopFilter', { top: top });
      await dispatch('loadCoinsList', false);
    },

    async activeBriefcaseFilter({ dispatch }){
      await dispatch('resetCompareData');
      dispatch('filter/activeBriefcaseFilter');
      await dispatch('loadCoinsList', false);
    },

    async activeCategoryFilter({ dispatch }, { tagId }){
      await dispatch('resetCompareData');
      dispatch('filter/activeCategoryFilter', { tagId: tagId });
      await dispatch('loadCoinsList', false);
    },

    async activeCustomTag({ dispatch }, { tagId }){
      await dispatch('resetCompareData');
      dispatch('filter/activeCustomTag', { tagId: tagId });
      await dispatch('loadCoinsList', false);
    },

    async loadCompareData({ dispatch, getters, commit, state }, compareCoin){
      const base_coin_id = getters['coinsList/getActiveCoinID'];
      commit('SET_IS_OPEN_INNER_CONTENT', true);
      commit('SET_COMPARE_COIN', compareCoin);
      if(getters['dynamics/isOpenedList']){
        await dispatch('dynamics/loadCompare', { base_coin_id: base_coin_id, compare_id: state.compareCoin.id });
      }
      if(getters['impulse/isOpenedList']){
        await dispatch('impulse/loadCompare', { base_coin_id: base_coin_id, compare_id: state.compareCoin.id });
      }
      if(getters['period/isOpenedList']){
        await dispatch('period/loadCompare', { base_coin_id: base_coin_id, compare_id: state.compareCoin.id});
      }
      if(getters['monthly/isOpenedList']){
        await dispatch('initMonthly');
      }
    },

    async resetCompareData({ dispatch, commit }){
      commit('SET_IS_OPEN_INNER_CONTENT', false);
      commit('SET_COMPARE_COIN', null);
      await dispatch('dynamics/resetCompare');
      await dispatch('impulse/resetCompare');
      await dispatch('period/resetCompare');
      await dispatch('monthly/resetCompare');
    },

    setPreselectedImpulseId({ commit }, id) {
      commit('SET_PRESELECTED_IMPULSE_ID', id);
    },
    //========================================================
    //             УПРАВЛЕНИЕ СПИСКОМ МОНЕТ
    //========================================================
    //Центральный вызов загрузки списка монет
    async loadCoinsList({ dispatch, commit, state, getters }, data) {
      const { context, isNext, idImpulse, period, monthlyPeriod } = data;
      //Устанавливаем контекст и флаги страницы
      if (context) commit('SET_COINS_LIST_CONTEXT', context);
      if (!isNext) {
        commit('SET_NEXT_PAGE_COINS_LIST_NUMBER', 1);
        commit('SET_LOADING_NEW_COINS_LIST_FLAG', true);
      }
      //Устанавливаем активные элементы, если переданы
      if (idImpulse) await dispatch('impulse/setActiveImpulseId', idImpulse);
      if (period && getters['getIsPeriodContext']){
        console.log('PERIOD')
        await dispatch('period/setActivePeriodId', period);
      } else if(period && getters['getIsDynamicContext']){
        console.log('DYNAMIC')
        await dispatch('dynamics/setActiveDynamicsId', period);
      }
      if (monthlyPeriod) await dispatch('monthly/setActiveMonthlyId', monthlyPeriod);
      //Делегируем обработку по контексту
      if (state.contextCoinsList === 'dynamics') {
        await dispatch('loadCoinsListDynamics');
      } else if (state.contextCoinsList === 'impulse') {
        await dispatch('loadCoinsListImpulse');
      } else if (state.contextCoinsList === 'period') {
        await dispatch('loadCoinsListPeriod');
      } else if (state.contextCoinsList === 'monthly') {
        await dispatch('loadCoinsListMonthly');
      }
    },

    //Вызов списка монет для динамики
    async loadCoinsListDynamics({ dispatch, state, getters, commit }) {
      commit('SET_LOADING_COINS_LIST_FLAG', true);
      const dynamics_id = getters['dynamics/activeDynamicId'] || getters['dynamics/allDynamics']?.[0]?.period;
      const params = {
        period: dynamics_id,
        custom_tag_id: getters['filter/getCustomTagId'],
        user_assets: getters['filter/getIsBriefcase'],
        base_coin_id: getters['coinsList/getActiveCoinID'],
        top: getters['filter/getTop'],
        page: state.nextPageCoinsListNumber,
        page_size: PAGE_SIZE,
        tag_id: getters['filter/getTagId'],
        usd: getters['coinsList/getIsUSD'],
      };
      try {
        const result = await dispatch('dynamics/loadCoinsList', params);
        await dispatch('handleCoinsListResult', result);
        return result;
      } catch (e) {
        console.error('Ошибка при загрузке списка монет (dynamics):', e);
      } finally {
        commit('SET_LOADING_COINS_LIST_FLAG', false);
        commit('SET_LOADING_NEW_COINS_LIST_FLAG', false);
      }
    },

    //Вызов списка монет для импульсов
    async loadCoinsListImpulse({ dispatch, state, getters, commit }) {
      commit('SET_LOADING_COINS_LIST_FLAG', true);
      const impulse_id = getters['impulse/activeImpulseId'] || getters['impulse/allImpulses']?.[0]?.impulse_id;
      const params = {
        impulse_id,
        custom_tag_id: getters['filter/getCustomTagId'],
        user_assets: getters['filter/getIsBriefcase'],
        base_coin_id: getters['coinsList/getActiveCoinID'],
        top: getters['filter/getTop'],
        page: state.nextPageCoinsListNumber,
        page_size: PAGE_SIZE,
        tag_id: getters['filter/getTagId'],
        usd: getters['coinsList/getIsUSD'],
      };
      try {
        const result = await dispatch('impulse/loadCoinsList', params);
        await dispatch('handleCoinsListResult', result);
        return result;
      } catch (e) {
        console.error('Ошибка при загрузке списка монет (impulse):', e);
      } finally {
        commit('SET_LOADING_COINS_LIST_FLAG', false);
        commit('SET_LOADING_NEW_COINS_LIST_FLAG', false);
      }
    },

    //Вызов списка монет для периодов
    async loadCoinsListPeriod({ dispatch, state, getters, commit }) {
      commit('SET_LOADING_COINS_LIST_FLAG', true);
      const period_id = getters['period/activePeriodId'] || getters['period/allPeriods']?.[0]?.period;
      const params = {
        period: period_id,
        custom_tag_id: getters['filter/getCustomTagId'],
        user_assets: getters['filter/getIsBriefcase'],
        base_coin_id: getters['coinsList/getActiveCoinID'],
        top: getters['filter/getTop'],
        page: state.nextPageCoinsListNumber,
        page_size: PAGE_SIZE,
        tag_id: getters['filter/getTagId'],
        usd: getters['coinsList/getIsUSD'],
      };
      try {
        const result = await dispatch('period/loadCoinsList', params);
        await dispatch('handleCoinsListResult', result);
        return result;
      } catch (e) {
        console.error('Ошибка при загрузке списка монет (period):', e);
      } finally {
        commit('SET_LOADING_COINS_LIST_FLAG', false);
        commit('SET_LOADING_NEW_COINS_LIST_FLAG', false);
      }
    },

    //Вызов списка монет для помесячных периодов
    async loadCoinsListMonthly({ dispatch, state, getters, commit }) {
      commit('SET_LOADING_COINS_LIST_FLAG', true);
      const monthly_id = getters['monthly/activeMonthlyId'] || getters['monthly/allMonthly']?.[0]?.period;
      const params = {
        period: monthly_id,
        custom_tag_id: getters['filter/getCustomTagId'],
        user_assets: getters['filter/getIsBriefcase'],
        base_coin_id: getters['coinsList/getActiveCoinID'],
        top: getters['filter/getTop'],
        page: state.nextPageCoinsListNumber,
        page_size: PAGE_SIZE,
        tag_id: getters['filter/getTagId'],
        usd: getters['coinsList/getIsUSD'],
      };
      try {
        const result = await dispatch('monthly/loadCoinsList', params);
        await dispatch('handleCoinsListResult', result);
        return result;
      } catch (e) {
        console.error('Ошибка при загрузке списка монет (monthly):', e);
      } finally {
        commit('SET_LOADING_COINS_LIST_FLAG', false);
        commit('SET_LOADING_NEW_COINS_LIST_FLAG', false);
      }
    },

    //Обработчик результатов
    async handleCoinsListResult({ commit, state }, result) {
      if (result && result.results) {
        const { results, next } = result;
        commit('SET_COINS_LIST_DATA', results);
        if (!next || next === state.nextPageCoinsListNumber) {
          commit('SET_NEXT_PAGE_COINS_LIST_FLAG', false);
        } else {
          commit('SET_NEXT_PAGE_COINS_LIST_FLAG', true);
          commit('SET_NEXT_PAGE_COINS_LIST_NUMBER', next);
        }
      } else {
        commit('SET_NEXT_PAGE_COINS_LIST_FLAG', false);
      }
      commit('SET_READY_COINS_LIST_FLAG', true);
      commit('SET_LOADING_COINS_LIST_FLAG', false);
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

    //Сброс данных
    async resetScanner({ commit, dispatch }) {
      commit('SET_COINS_LIST_DATA', []);
      commit('SET_COMPARE_COIN', null);
      commit('SET_IS_OPEN_INNER_CONTENT', false);
      commit('SET_NEXT_PAGE_COINS_LIST_FLAG', true);
      commit('SET_NEXT_PAGE_COINS_LIST_NUMBER', 1);
      commit('SET_READY_COINS_LIST_FLAG', false);
      commit('SET_LOADING_COINS_LIST_FLAG', false);
      commit('SET_LOADING_NEW_COINS_LIST_FLAG', false);
      commit('SET_PRESELECTED_IMPULSE_ID', null);
      commit('SET_COINS_LIST_CONTEXT', 'dynamics');

      await dispatch('dynamics/resetDynamics');
      await dispatch('impulse/resetImpulse');
      await dispatch('period/resetPeriod');
      await dispatch('monthly/resetMonthly');
      await dispatch('filter/resetFilter');
    },
  },

  mutations: {
    SET_COINS_LIST_DATA(state, data) {
      if (state.nextPageCoinsListNumber === 1) {
        state.coins = data;
      } else {
        state.coins = [...state.coins, ...data];
      }
    },

    SET_IS_OPEN_INNER_CONTENT(state, status) { state.isOpenInnerContent = status; },

    SET_COINS_LIST_CONTEXT(state, context){ state.contextCoinsList = context; },

    SET_LOADING_COINS_LIST_FLAG(state, status){ state.isLoadingCoinsList = status; },

    SET_NEXT_PAGE_COINS_LIST_FLAG(state, status){ state.isNextCoinsList = status; },

    SET_NEXT_PAGE_COINS_LIST_NUMBER(state, number){ state.nextPageCoinsListNumber = number; },

    SET_READY_COINS_LIST_FLAG(state, status){ state.isReadyCoinsList = status; },

    SET_COMPARE_COIN(state, data){ state.compareCoin = data},

    SET_LOADING_NEW_COINS_LIST_FLAG(state, status){ state.isLoadingNewCoinsList = status; },

    SET_PRESELECTED_IMPULSE_ID(state, id) { state.preselectedImpulseId = id; }
  },

  getters: {
    //Возвращает активный список монет
    getCoinsList: (state) => state.coins,
    //Идет ли подгрузка списка монет
    getIsLoadingCoinsList: (state) => state.isLoadingCoinsList,
    //Готов ли список монет к отображению
    getIsReadyCoinsList: (state) => state.isReadyCoinsList,
    //Есть ли следующая страница прогрузки для списка монет
    hasNextCoinsPage: (state) => state.isNextCoinsList,
    //Номер следующей страницы
    getNextPageCoinsListNumber: (state) => state.nextPageCoinsListNumber,
    //Текущий контекст принадлежит импульсам?
    getIsImpulseContext: (state) => state.contextCoinsList === 'impulse',
    //Текущий контекст принадлежит периодам?
    getIsPeriodContext: (state) => state.contextCoinsList === 'period',
    //Текущий контекст принадлежит к помесячным периодам?
    getIsMonthlyContext: (state) => state.contextCoinsList === 'monthly',
    //Текущий контекст принадлежит к динамике
    getIsDynamicContext: (state) => state.contextCoinsList === 'dynamics',
    //Открыта ли внутренняя страница
    getIsOpenedInnerPage: (state) => state.isOpenInnerContent,
    //Получить объект compare coin
    getCompareCoin: (state) => state.compareCoin,
    //Получить статус загрузки следующей страницы спсика монет
    getIsLoadingNextCoinsList: (state) => state.isLoadingNewCoinsList,
  },

  modules: {
    dynamics,
    impulse,
    period,
    monthly,
    filter,
    coinsList,
    widget
  },
};

export default Scanner;